import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from '@emotion/styled';

import { BoxShadow } from '../../../../shared/GlobalStyles';
import DropDown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import AssignedCompletedChart from './AssignedCompletedChart';
import ExerciseFilter from './ExerciseFilter';
import OverviewChart from './OverviewChart';
import { setSelectedAthlete } from '../../ducks/volumeTrackerDashboardActions';

const DashboardContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin: 15px 15px 7px 15px;
  background-color: #FFFFFF;
  border-radius: 10px;
  padding: 16px;
  box-shadow: ${BoxShadow};
  position: relative;
`;

const OptionsHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const MenuOptionsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const DateOptionsContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  margin-right: 10px;
`;

const FilterWrapper = styled('div')`
  display: flex;
  margin: 5px 0;
`;

const NoDataModal = styled('div')`
  position: absolute;
  text-align: center;
  top: 50%;
  left: 50%;
  margin: -45px 0px 0px -182px;
  height: 119px;
  width: 400px;
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  font-family: 'Nunito Sans';
  font-size: 19px;
  color: #424242;
  padding: 15px;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const ModalMessage = styled('div')`
  font-weight: 300;
`;

const ViewOnlyVTChartWrapper = ({ chartType }) => {
  const dispatch = useDispatch();

  const athleteList = useSelector(
    (state) => state.volumeTrackerDashboard.data.athleteList,
  );
  const bucketSize = useSelector(
    (state) => state.volumeTrackerDashboard.data.bucketSize,
  );
  const endDate = useSelector(
    (state) => state.volumeTrackerDashboard.data.endDateCal,
  );
  const exerciseFilters = useSelector(
    (state) => state.volumeTrackerDashboard.data.exerciseFilters,
  );
  const group = useSelector(
    (state) => state.volumeTrackerDashboard.data.group,
  );
  const rangeMax = useSelector(
    (state) => state.volumeTrackerDashboard.data[
      chartType === 'assigned' ? 'assignedCompletedRangeMax' : 'overviewRangeMax'
    ],
  );
  const rangeMin = useSelector(
    (state) => state.volumeTrackerDashboard.data[
      chartType === 'assigned' ? 'assignedCompletedRangeMin' : 'overviewRangeMin'
    ],
  );
  const selectedAthlete = useSelector(
    (state) => state.volumeTrackerDashboard.data.selectedAthlete,
  );
  const selectedExerciseNames = useSelector(
    (state) => state.volumeTrackerDashboard.data.exerciseFilters.map((ex) => ex.name),
  );
  const startDate = useSelector(
    (state) => state.volumeTrackerDashboard.data.startDateCal,
  );
  const vtDashData = useSelector(
    (state) => state.volumeTrackerDashboard.data.vtDashData,
  );

  const isVTDashDataLoading = useSelector(
    (state) => state.volumeTrackerDashboard.ui.isVTDashDataLoading,
  );

  const ChartComponent = chartType === 'assigned'
    ? AssignedCompletedChart
    : OverviewChart;

  const hasChartData = !!vtDashData?.length;
  const athleteDropdownList = hasChartData
    ? [
      { fullName: 'View Group', userId: null },
      ...[...athleteList]
        .sort((a, b) => a.last.localeCompare(b.last))
        .map((athlete) => ({
          fullName: `${athlete.first} ${athlete.last}`,
          userId: athlete.id,
          ...athlete,
        })),
    ]
    : [];

  const setAthlete = (option) => {
    if (option.fullName === 'View Group') {
      dispatch(setSelectedAthlete(null, false));
    } else {
      dispatch(setSelectedAthlete(option, true));
    }
  };

  return (
    <>
      <DashboardContainer>
        {hasChartData && (
          <OptionsHeader>
            <MenuOptionsContainer>
              <DropDown
                disabled
                list={['']}
                label='name'
                headerWidthPx={175}
                placeholder='Select a Group'
                defaultOption={group || {}}
                readOnly
              />
              <DropDown
                list={athleteDropdownList}
                label='fullName'
                selectItemFunction={setAthlete}
                headerWidthPx={175}
                placeholder='Select an Athlete'
                defaultOption={selectedAthlete || {}}
              />
              <DateOptionsContainer>
                <DatePicker
                  singleDate={false}
                  startDate={startDate ? moment(startDate).toDate() : null}
                  endDate={endDate ? moment(endDate).toDate() : null}
                  maxDate={moment().toDate()}
                  minDate={null}
                  disabled
                />
              </DateOptionsContainer>
              <FilterWrapper>
                <ExerciseFilter viewOnly />
              </FilterWrapper>
            </MenuOptionsContainer>
          </OptionsHeader>
        )}
        {exerciseFilters?.length > 0 ? (
          exerciseFilters.map((filter, index) => {
            const matchingData = vtDashData?.find((d) => d.exerciseName === filter.name);
            return (
              <ChartComponent
                key={filter.name}
                exerciseData={matchingData || { exerciseName: filter.name, dates: [] }}
                bucketSize={bucketSize}
                rangeMax={rangeMax}
                rangeMin={rangeMin}
                selectedAthlete={selectedAthlete}
                isLoading={isVTDashDataLoading && index === 0}
                viewOnly
              />
            );
          })
        ) : (
          <ChartComponent exerciseData={null} rangeMin={rangeMin} isLoading={isVTDashDataLoading} />
        )}
        {!isVTDashDataLoading && selectedExerciseNames.length === 0 && (
          <NoDataModal>
            <ModalTitle>No Data Available</ModalTitle>
            <ModalMessage>
              There is no data available. Please select an exercise to get started.
            </ModalMessage>
          </NoDataModal>
        )}
      </DashboardContainer>
    </>
  );
};

ViewOnlyVTChartWrapper.propTypes = {
  chartType: PropTypes.oneOf(['assigned', 'overview']).isRequired,
};

export default ViewOnlyVTChartWrapper;
